import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Plus, Trash2, Check, Download } from 'lucide-react';
import { TextInput, TextArea } from '../FormElements';
interface ContractItem {
  id: string;
  type: 'need' | 'offer';
  description: string;
  value?: string;
}
interface ContractBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  role: 'brand' | 'organizer';
  partnerName?: string;
}
export function ContractBuilder({
  isOpen,
  onClose,
  role,
  partnerName = 'Partner'
}: ContractBuilderProps) {
  const [items, setItems] = useState<ContractItem[]>([]);
  const [newItem, setNewItem] = useState({
    type: 'need' as 'need' | 'offer',
    description: '',
    value: ''
  });
  const [contractTitle, setContractTitle] = useState('');
  const [agreed, setAgreed] = useState(false);
  const buttonColor = role === 'brand' ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900' : 'bg-purple-600 hover:bg-purple-500 text-white';
  const accentBg = role === 'brand' ? 'bg-cyan-500/10' : 'bg-purple-500/10';
  const accentText = role === 'brand' ? 'text-cyan-400' : 'text-purple-400';
  const accentBorder = role === 'brand' ? 'border-cyan-500' : 'border-purple-500';
  const addItem = () => {
    if (!newItem.description.trim()) return;
    setItems([...items, {
      ...newItem,
      id: Date.now().toString()
    }]);
    setNewItem({
      type: 'need',
      description: '',
      value: ''
    });
  };
  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };
  const needs = items.filter(i => i.type === 'need');
  const offers = items.filter(i => i.type === 'offer');
  return <AnimatePresence data-id="element-108">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-109">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} onClick={e => e.stopPropagation()} className="w-full max-w-3xl max-h-[90vh] bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-id="element-110">
            <div className="p-6 border-b border-slate-800 flex items-center justify-between" data-id="element-111">
              <div className="flex items-center gap-3" data-id="element-112">
                <div className={`p-2 rounded-lg ${accentBg}`} data-id="element-113">
                  <FileText size={20} className={accentText} data-id="element-114" />
                </div>
                <div data-id="element-115">
                  <h2 className="text-lg font-bold text-white" data-id="element-116">
                    Contract Builder
                  </h2>
                  <p className="text-sm text-slate-400" data-id="element-117">
                    Define needs & offers with {partnerName}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-118">
                <X size={20} data-id="element-119" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6" data-id="element-120">
              <div className="mb-6" data-id="element-121">
                <TextInput label="Contract Title" placeholder="e.g. Tech Summit 2024 Sponsorship Agreement" value={contractTitle} onChange={e => setContractTitle(e.target.value)} data-id="element-122" />
              </div>

              {/* Add New Item */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6" data-id="element-123">
                <h3 className="text-sm font-medium text-slate-300 mb-3" data-id="element-124">
                  Add Item
                </h3>
                <div className="flex gap-2 mb-3" data-id="element-125">
                  <button onClick={() => setNewItem({
                ...newItem,
                type: 'need'
              })} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${newItem.type === 'need' ? `${accentBg} ${accentText} border ${accentBorder}` : 'bg-slate-700 text-slate-400 border border-transparent'}`} data-id="element-126">
                    I Need
                  </button>
                  <button onClick={() => setNewItem({
                ...newItem,
                type: 'offer'
              })} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${newItem.type === 'offer' ? `${accentBg} ${accentText} border ${accentBorder}` : 'bg-slate-700 text-slate-400 border border-transparent'}`} data-id="element-127">
                    I Offer
                  </button>
                </div>
                <div className="flex gap-3" data-id="element-128">
                  <input type="text" placeholder="Description (e.g. Logo placement on main stage)" value={newItem.description} onChange={e => setNewItem({
                ...newItem,
                description: e.target.value
              })} className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-slate-600" data-id="element-129" />
                  <input type="text" placeholder="Value (optional)" value={newItem.value} onChange={e => setNewItem({
                ...newItem,
                value: e.target.value
              })} className="w-32 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-slate-600" data-id="element-130" />
                  <button onClick={addItem} className={`p-2 rounded-lg transition-colors ${buttonColor}`} data-id="element-131">
                    <Plus size={20} data-id="element-132" />
                  </button>
                </div>
              </div>

              {/* Needs & Offers Lists */}
              <div className="grid md:grid-cols-2 gap-6" data-id="element-133">
                <div data-id="element-134">
                  <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3" data-id="element-135">
                    Needs
                  </h3>
                  {needs.length === 0 ? <p className="text-slate-500 text-sm py-4" data-id="element-136">
                      No needs added yet
                    </p> : <div className="space-y-2" data-id="element-137">
                      {needs.map(item => <div key={item.id} className="bg-slate-800 border border-slate-700 rounded-lg p-3 flex items-center justify-between group" data-id="element-138">
                          <div data-id="element-139">
                            <p className="text-white" data-id="element-140">{item.description}</p>
                            {item.value && <p className="text-sm text-slate-400" data-id="element-141">
                                {item.value}
                              </p>}
                          </div>
                          <button onClick={() => removeItem(item.id)} className="p-1 text-slate-500 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-all" data-id="element-142">
                            <Trash2 size={16} data-id="element-143" />
                          </button>
                        </div>)}
                    </div>}
                </div>

                <div data-id="element-144">
                  <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3" data-id="element-145">
                    Offers
                  </h3>
                  {offers.length === 0 ? <p className="text-slate-500 text-sm py-4" data-id="element-146">
                      No offers added yet
                    </p> : <div className="space-y-2" data-id="element-147">
                      {offers.map(item => <div key={item.id} className="bg-slate-800 border border-slate-700 rounded-lg p-3 flex items-center justify-between group" data-id="element-148">
                          <div data-id="element-149">
                            <p className="text-white" data-id="element-150">{item.description}</p>
                            {item.value && <p className="text-sm text-slate-400" data-id="element-151">
                                {item.value}
                              </p>}
                          </div>
                          <button onClick={() => removeItem(item.id)} className="p-1 text-slate-500 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-all" data-id="element-152">
                            <Trash2 size={16} data-id="element-153" />
                          </button>
                        </div>)}
                    </div>}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-800" data-id="element-154">
              <label className="flex items-center gap-3 mb-4 cursor-pointer" data-id="element-155">
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/20" data-id="element-156" />
                <span className="text-slate-300 text-sm" data-id="element-157">
                  I agree to the terms outlined in this contract
                </span>
              </label>
              <div className="flex gap-3" data-id="element-158">
                <button onClick={onClose} className="flex-1 py-3 rounded-full font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" data-id="element-159">
                  Save Draft
                </button>
                <button disabled={!agreed || items.length === 0} className={`flex-1 py-3 rounded-full font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${buttonColor}`} data-id="element-160">
                  <Check size={18} data-id="element-161" /> Send for Approval
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}